import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItem,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, warningAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import DocumentSubcategory, {
  newDocumentSubcategory,
} from "../../models/document-subcategory";
import DocumentCategory from "../../models/document-category";
import DocumentCategorySelect from "../document-category/DocumentCategorySelect";

interface DocumentSubcategoryFormProps {
  initialDocumentSubcategory?: DocumentSubcategory;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const DocumentSubcategoryForm: React.FC<DocumentSubcategoryFormProps> = ({
  initialDocumentSubcategory,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingDocumentSubcategory, setEditingDocumentSubcategory] =
    useState<DocumentSubcategory>(
      initialDocumentSubcategory
        ? initialDocumentSubcategory
        : newDocumentSubcategory()
    );
  const [selectedDocumentCategory, setSelectedDocumentCategory] = useState<
    DocumentCategory | undefined
  >(undefined);
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDocumentSubcategory({
      ...editingDocumentSubcategory,
      name: e.target.value,
    });
  };

  const onDocumentCategoryChange = (
    newDocumentCategory: DocumentCategory | null
  ) => {
    setSelectedDocumentCategory(
      newDocumentCategory !== null ? newDocumentCategory : undefined
    );
    setEditingDocumentSubcategory({
      ...editingDocumentSubcategory,
      categoryId:
        newDocumentCategory !== null ? newDocumentCategory.id : undefined,
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let toSendDocumentSubcategory = {
      ...editingDocumentSubcategory,
      companyId: company.id,
    };
    let requestPromise: Promise<ItemRequestStatus<DocumentSubcategory>>;
    if (editingDocumentSubcategory.id === undefined) {
      requestPromise = createItem<DocumentSubcategory>(
        "/document_subcategories/create/",
        toSendDocumentSubcategory
      );
    } else {
      requestPromise = updateItem<DocumentSubcategory>(
        `/document_subcategories/${toSendDocumentSubcategory.id}/`,
        toSendDocumentSubcategory
      );
    }

    const documentSubcategoriestatus = await requestPromise;

    if (documentSubcategoriestatus.status !== SUCCESS) {
      if (documentSubcategoriestatus.errors !== undefined) {
        setErrors(documentSubcategoriestatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (documentSubcategoriestatus.detail !== undefined) {
        message = documentSubcategoriestatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingDocumentSubcategory(newDocumentSubcategory());
    setSelectedDocumentCategory(undefined);
  };

  const fetchDocumentCategory = useCallback(
    async (id: number) => {
      const categoryStatus = await getItem<DocumentCategory>(
        `/document_categories/${id}/`
      );
      if (categoryStatus.status === SUCCESS) {
        if (categoryStatus.data !== undefined) {
          setSelectedDocumentCategory(categoryStatus.data);
        }
      } else {
        const message = categoryStatus.detail
          ? categoryStatus.detail
          : "Error desconocido";
        warningAlert(message);
      }
    },
    [initialDocumentSubcategory]
  );

  useEffect(() => {
    setEditingDocumentSubcategory(
      initialDocumentSubcategory
        ? initialDocumentSubcategory
        : newDocumentSubcategory()
    );
    if (initialDocumentSubcategory?.categoryId) {
      fetchDocumentCategory(initialDocumentSubcategory.categoryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDocumentSubcategory]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingDocumentSubcategory.name)}
                onChange={onNameChange}
                placeholder="Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={2}>
              <CLabel className={"mt-2"}>Categoría Padre:</CLabel>
            </CCol>
            <CCol>
              <DocumentCategorySelect
                value={
                  selectedDocumentCategory ? selectedDocumentCategory : null
                }
                onChange={onDocumentCategoryChange}
              ></DocumentCategorySelect>
              <FieldErrors
                errors={getFieldErrors("categoryId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default DocumentSubcategoryForm;
